import React, { useEffect, useRef, useState } from "react";
import { Plus, Minus } from "lucide-react";

/**
 * NumberInput — controlled numeric input with a great typing UX.
 * - Empty while editing (no forced 0 on every keystroke)
 * - Strips non-digits and leading zeros
 * - Validates on blur (empty → falls back to `min`, or stays empty if `emptyOk`)
 * - + / − buttons, ArrowUp / ArrowDown increment
 * - Emits numeric values to parent (never strings)
 *
 * Props:
 *   value      number | undefined       - controlled value
 *   onChange   (n:number) => void
 *   min        number (default 0)
 *   max        number (default Infinity)
 *   step       number (default 1)
 *   placeholder, testid, disabled
 *   emptyOk    boolean (default false)  - if true, blur can leave empty (emits 0)
 *   compact    boolean                  - narrower stepper for tight UIs
 */
export default function NumberInput({
  value,
  onChange,
  min = 0,
  max = Infinity,
  step = 1,
  placeholder = "",
  testid,
  disabled,
  emptyOk = false,
  compact = false,
  className = "",
}) {
  // Local text state so users can freely clear the field while editing.
  const [text, setText] = useState(value === undefined || value === null ? "" : String(value));
  const [focused, setFocused] = useState(false);
  const inputRef = useRef(null);

  // Sync from parent only when not focused (prevents cursor jumps mid-typing).
  useEffect(() => {
    if (focused) return;
    const next = value === undefined || value === null || Number.isNaN(value) ? "" : String(value);
    setText(next);
  }, [value, focused]);

  const clamp = (n) => Math.min(max, Math.max(min, n));

  const setValue = (n) => {
    const clamped = clamp(Number.isFinite(n) ? n : min);
    setText(String(clamped));
    onChange(clamped);
  };

  const handleChange = (e) => {
    let v = e.target.value;
    // Keep digits only (allow empty)
    v = v.replace(/[^0-9]/g, "");
    // Strip leading zeros unless the field is exactly "0"
    v = v.replace(/^0+(\d)/, "$1");
    if (v === "0" && min > 0) v = ""; // hint to type a real value; blur will fix
    setText(v);
    if (v === "") {
      // Stay silent while editing — don't push 0 to parent every keystroke
      return;
    }
    const n = parseInt(v, 10);
    if (!Number.isNaN(n)) {
      const capped = n > max ? max : n;
      if (capped !== n) setText(String(capped));
      onChange(capped);
    }
  };

  const handleBlur = () => {
    setFocused(false);
    if (text === "") {
      if (emptyOk) { onChange(0); return; }
      // Reset to min
      setValue(min);
      return;
    }
    const n = parseInt(text, 10);
    setValue(Number.isNaN(n) ? min : n);
  };

  const handleKeyDown = (e) => {
    if (e.key === "ArrowUp") {
      e.preventDefault();
      setValue((Number.isFinite(value) ? value : min - 1) + step);
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      setValue((Number.isFinite(value) ? value : min + 1) - step);
    }
  };

  const bump = (delta) => {
    const current = Number.isFinite(value) ? value : (min || 0);
    setValue(current + delta);
    inputRef.current?.focus();
  };

  const btn = `inline-flex items-center justify-center border border-border ${compact ? "w-7 h-8" : "w-8 h-9"} rounded text-slate-600 hover:bg-slate-50 hover:border-emerald-500 disabled:opacity-40`;

  return (
    <div className={`inline-flex items-stretch gap-1 ${className}`}>
      <button
        type="button"
        data-testid={testid ? `${testid}-dec` : undefined}
        onClick={() => bump(-step)}
        disabled={disabled || (Number.isFinite(value) && value <= min)}
        className={btn}
        aria-label="Decrease"
      >
        <Minus size={13} />
      </button>
      <input
        ref={inputRef}
        data-testid={testid}
        type="text"
        inputMode="numeric"
        pattern="[0-9]*"
        value={text}
        onChange={handleChange}
        onFocus={() => setFocused(true)}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        disabled={disabled}
        className={`text-center border border-border rounded ${compact ? "w-14 h-8 text-sm" : "w-16 h-9 text-sm"} focus:border-emerald-500 focus:outline-none`}
      />
      <button
        type="button"
        data-testid={testid ? `${testid}-inc` : undefined}
        onClick={() => bump(step)}
        disabled={disabled || (Number.isFinite(value) && value >= max)}
        className={btn}
        aria-label="Increase"
      >
        <Plus size={13} />
      </button>
    </div>
  );
}
